import type {
  TradeOrderAmountSpecVersion,
  TradeOrderSpecVersion,
  TradeOrderUnitSpecVersion,
} from '@festina/unitlink-general-types/unitlink'
import { notEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { useHook } from '../../lenteHooks'
import { FundLink } from '../../pages/fund/FundLink'
import { formatUnit } from '../../pages/portfolio/utility'

type Props = {
  tradeCycleId: string
}

const isTradeOrderAmountSpecVersion = (
  trade: TradeOrderSpecVersion,
): trade is TradeOrderAmountSpecVersion =>
  trade._type === 'TradeOrderAmountSpecVersion'

const isTradeOrderUnitSpecVersion = (
  trade: TradeOrderSpecVersion,
): trade is TradeOrderUnitSpecVersion =>
  trade._type === 'TradeOrderUnitSpecVersion'

type TradeRow = TradeOrderSpecVersion & {
  actualAmount?: Optional<number>
  units?: Optional<number>
}

const defineColumn = createColumnDefiner<TradeRow>()

export const TradeOrdersCard = ({ tradeCycleId }: Props) => {
  const translate = useTranslate()
  const { formatWithDecimals: formatCurrency } = useAmountFormatter()

  const tenantId = useHook('routing.tenant.useTenantParam')()

  const { data: tradeOrders } = useHook(
    'fetchers.unitlink.useFetchTradeOrders',
  )({
    tenantId,
    tradeCycleId,
  })

  const trades = useMemo(
    () => tradeOrders?.trades?.filter(notEmpty) ?? [],
    [tradeOrders],
  )

  const groupedTrades = useMemo(() => {
    const grouped = new Map<string, TradeRow>()
    trades.forEach((trade) => {
      const key = `${trade.isinKey}`
      const group = grouped.get(key)

      const modifiedTrade = structuredClone(trade)

      if (isTradeOrderAmountSpecVersion(trade)) {
        Object.assign(modifiedTrade, { actualAmount: trade.amount })
      } else if (isTradeOrderUnitSpecVersion(trade)) {
        Object.assign(modifiedTrade, { units: trade.amount })
      }

      grouped.set(key, { ...group, ...modifiedTrade })
    })

    return [
      ...grouped.values().filter((x) => !!x.actualAmount || !!x.units),
    ].sort((a, b) => (a.isinKey ?? '').localeCompare(b.isinKey ?? ''))
  }, [trades])

  const columns = useMemo(
    () => ({
      fundName: defineColumn({
        heading: translate('portfolio.cash.fundName'),
        keyPath: 'fundName',
      }),
      isinKey: defineColumn({
        heading: translate('portfolio.cash.isinKey'),
        keyPath: 'isinKey',
        formatter: (isinKey) =>
          isinKey ? <FundLink tenantId={tenantId} isinKey={isinKey} /> : '-',
      }),
      units: defineColumn({
        heading: translate('tradeCycle.cycle.units'),
        keyPath: 'units',
        formatter: formatUnit,
        alignment: 'right',
      }),
      amount: defineColumn({
        heading: translate('tradeCycle.cycle.amount'),
        keyPath: 'actualAmount',
        formatter: formatCurrency,
        alignment: 'right',
      }),
    }),
    [formatCurrency, tenantId, translate],
  )

  return trades.length ? (
    <Card>
      <CardHeader title={translate('portfolio.trade.orders')} />
      <CardBody>
        <SortableTable data={groupedTrades} columns={columns} />
      </CardBody>
    </Card>
  ) : null
}
